/**
 * Message Requests (user-management §3.3, screen B8): first DMs from people
 * who aren't your friends wait here. The preview hides media and makes links
 * inert until you accept. Accept moves the DM into your list; Dismiss removes
 * it for good; Block blocks the sender; Report (Console v2 §3.3) sends the
 * request's message, or the sender when it has none, to Neosocial's Safety
 * team. Nothing tells the sender which one you chose. (The Spam folder is
 * P2: the server has none yet.)
 */
import type { Channel } from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { updateData, useData } from '../store/data';
import { displayName } from '../store/selectors';
import { goHub } from '../store/ui';
import { requestFromChannel, setRequests, useUm, type MessageRequest } from '../store/um';
import { Avatar, Btn, EmptyState, Icon } from '../ui/kit';
import { toast } from '../ui/overlay';
import { runAction } from '../mod/actions';
import { confirmBlock } from './social';
import { openReport } from '../report/openReport';

/** The preview text: links never become clickable and are shown as a placeholder. */
export function inertPreview(text: string, linkLabel: string): string {
  return text.replace(/\b(?:https?:\/\/|www\.)\S+/giu, linkLabel).slice(0, 300);
}

/**
 * Takes a request out of the inbox. Idempotent: the server's
 * NX_MESSAGE_REQUEST_UPDATE may already have taken it out (and lowered the
 * badge) before the REST answer arrives, so the badge drops once, not twice.
 */
export function dropRequest(channelId: string): void {
  useUm.setState((s) => {
    const had = s.requests?.some((x) => x.channel.id === channelId) ?? false;
    if (!had) return s;
    return {
      requests: s.requests!.filter((x) => x.channel.id !== channelId),
      requestCount: Math.max(0, s.requestCount - 1),
    };
  });
}

function RequestRow({ r }: { r: MessageRequest }) {
  const { t, fmt } = useT();
  const [busy, setBusy] = useState<string | null>(null);
  const name = displayName(r.user);
  const m = r.last_message;
  const markChannel = (state: 'accepted' | 'dismissed', ch?: Channel | null) =>
    updateData((s) => ({
      ...s,
      channels: {
        ...s.channels,
        [r.channel.id]: {
          ...(ch ?? s.channels[r.channel.id] ?? r.channel),
          nx_request_state: state,
        },
      },
    }));
  const act = async (action: 'accept' | 'dismiss') => {
    setBusy(action);
    const state = action === 'accept' ? 'accepted' : 'dismissed';
    const res = await runAction(() => api.um.setRequest(r.channel.id, { state }));
    setBusy(null);
    if (!res.ok) return;
    dropRequest(r.channel.id);
    markChannel(state, res.value);
    if (action === 'accept') {
      toast(t('um.requests.accepted', { name }), { icon: 'check', tone: 'ok' });
      goHub({ kind: 'home', channelId: r.channel.id });
    } else toast(t('um.requests.dismissed'));
  };
  return (
    <li className="ns-req">
      <Avatar user={r.user} name={name} size={40} />
      <div className="ns-req-b">
        <div className="ns-req-h">
          <b dir="auto">{name}</b>
          <span className="ns-muted" dir="ltr">
            {r.user.username}
          </span>
          <time className="ns-muted" dateTime={r.created_at}>
            {fmt.stamp(r.created_at)}
          </time>
        </div>
        {m ? (
          <p className="ns-req-t" dir="auto">
            {inertPreview(m.content, t('um.requests.link'))}
            {m.attachments.length ? (
              <span className="ns-muted">
                {' '}
                {t('um.requests.media', { n: m.attachments.length })}
              </span>
            ) : null}
          </p>
        ) : null}
        <div className="ns-req-acts">
          <Btn kind="primary" size="xs" busy={busy === 'accept'} onClick={() => void act('accept')}>
            {t('requests.accept')}
          </Btn>
          <Btn kind="ghost" size="xs" busy={busy === 'dismiss'} onClick={() => void act('dismiss')}>
            {t('requests.dismiss')}
          </Btn>
          <Btn
            kind="danger"
            size="xs"
            busy={busy === 'block'}
            onClick={async () => {
              setBusy('block');
              // the server also takes the request out of Requests for good
              const ok = await confirmBlock(r.user.id, name);
              setBusy(null);
              if (!ok) return;
              dropRequest(r.channel.id);
              markChannel('dismissed');
            }}
          >
            {t('ns.fr.block')}
          </Btn>
          <Btn
            kind="ghost"
            size="xs"
            icon="flag"
            onClick={() =>
              openReport(m ? { kind: 'message', message: m } : { kind: 'user', user: r.user })
            }
          >
            {t('rp.report')}
          </Btn>
        </div>
      </div>
    </li>
  );
}

export function RequestsPage() {
  const { t } = useT();
  const list = useUm((s) => s.requests);
  const [failed, setFailed] = useState(false);
  const meId = useData((d) => d.me?.id);
  useEffect(() => {
    let live = true;
    api.um
      .messageRequests()
      .then(async (chs) => {
        // each preview is the request's newest message; one that fails stays without one
        const views = await Promise.all(
          chs.map(async (c) =>
            requestFromChannel(c, await api.um.lastMessage(c.id).catch(() => null)),
          ),
        );
        if (live) setRequests(views.filter((v): v is MessageRequest => !!v));
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [meId]);
  const pending = list ?? [];
  return (
    <section className="hub-pane nx-panel ns-requests" aria-label={t('requests.title')}>
      <header className="ch-head">
        <div className="ch-title">
          <span className="ch-title-i" aria-hidden="true">
            <Icon name="inbox" />
          </span>
          <h1>{t('requests.title')}</h1>
        </div>
      </header>
      <div className="ns-requests-body nx-scroll">
        <p className="ns-note">
          <Icon name="eye" /> {t('requests.preview')}
        </p>
        {failed && !list ? <p className="ns-err">{t('um.requests.loadFailed')}</p> : null}
        {!list && !failed ? (
          <p className="ns-muted">
            <i className="nx-spin" /> {t('ns.loading')}
          </p>
        ) : null}
        {list && !pending.length ? (
          <EmptyState icon="inbox" title={t('requests.empty')} hint={t('people.req.emptyHint')} />
        ) : null}
        <ul className="ns-req-list">
          {pending.map((r) => (
            <RequestRow key={r.channel.id} r={r} />
          ))}
        </ul>
      </div>
    </section>
  );
}
